import React, { useEffect, useState } from 'react';

import { graphql, useStaticQuery } from 'gatsby';
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import GlobalStyle from 'styles/GlobalStyles';

import Footer from 'components/Layout/Footer/Footer';
import CampaignHeader from 'components/Layout/Header/CampaignHeader';
import Header from 'components/Layout/Header/Header';
import ModalWithActions from 'components/Modal/ModalWithActions';

import useErrorBoundary from 'hooks/useErrorBoundary';
import useScroll from 'hooks/useScroll';
import useScrollTracking from 'hooks/useScrollTracking';

import { ModalI } from 'interfaces/content-stack/contentStack.types';
import { ImportMap } from 'interfaces/content-stack/importMap';

import { NEW_USER, RETURN_USER } from 'utils/constants';
import { dataLayerPush } from 'utils/functions';

const Container = styled.div<{ $alertOpen: boolean }>``;

const MainWrapper = styled.div<{ $scrollUp?: boolean; $scrollY?: number }>`
  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    transform: ${(props) => (props.$scrollUp && scrollY < 100 ? 'translateY(-2rem)' : 'none')};
    transition: all 0.5s;
  }
`;

const Layout = ({
  children,
  pageType,
  hideFooterMenu = false,
  disableAlert,
}: {
  children: React.ReactNode;
  pageType?: string;
  hideFooterMenu?: boolean;
  disableAlert?: boolean;
}) => {
  const { scrollDirection, scrollY } = useScroll();
  useErrorBoundary();
  useScrollTracking();

  const data = useStaticQuery(graphql`
    query {
      contentstackFooter {
        ...FooterFragment
      }
      contentstackLayoutBody {
        ...BodyFragment
      }
      contentstackAlertBanner {
        ...AlertBannerFragment
      }
      allImportMap {
        nodes {
          ...ImportMapFragment
        }
      }
    }
  `);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const visitorCookie = Cookies.get('visitorCookie');
  const [initialVisit] = useState(typeof visitorCookie === 'undefined' ? true : false);

  useEffect(() => {
    if (initialVisit) {
      Cookies.set('newUserDateCookie', new Date().toISOString(), { expires: 3650 });
      Cookies.set('visitorCookie', NEW_USER, { expires: 3650 });
      dataLayerPush('new_user', 'visitor_status');
    } else {
      Cookies.set('visitorCookie', RETURN_USER, { expires: 3650 });
      dataLayerPush('return_user', 'visitor_status');
    }
  }, []);

  useEffect(() => {
    if (disableAlert) {
      setIsAlertOpen(false);
    } else {
      setIsAlertOpen(data.contentstackAlertBanner.siteAlerts.isActive && !sessionStorage.getItem('closedAlert'));
    }
  }, []);

  const rebrandModalData = data?.contentstackLayoutBody.modals.find((modal: ModalI) => modal.modalType === 'rebrand');
  const HeaderComponent = pageType === 'Promotions/Campaigns' ? CampaignHeader : Header;

  return (
    <Container $alertOpen={isAlertOpen}>
      <Helmet>
        {data.allImportMap.nodes.map((node: ImportMap) => {
          return <script type='systemjs-importmap'>{JSON.stringify(node.map)}</script>;
        })}
      </Helmet>
      <GlobalStyle />
      <HeaderComponent
        isAlertOpen={isAlertOpen}
        setIsAlertOpen={setIsAlertOpen}
        contentstackAlertBanner={data.contentstackAlertBanner}
      />
      <ModalWithActions data={rebrandModalData} />
      <MainWrapper $scrollY={scrollY} $scrollUp={scrollDirection === 'up'}>
        {children}
      </MainWrapper>
      <Footer data={data?.contentstackFooter} hideFooterMenu={hideFooterMenu} />
    </Container>
  );
};

export default Layout;
